import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, useRef, useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { common } from '../../assets/js/common.js';
import { DialogWrapper } from './dialog-wrapper.js';
import { Calendar } from '../ui/calendar.js';
import { LineIcon } from '../ui/icons/LineIcon.js';
import { useDevice } from '../../contexts/DeviceContext.js';
import { useInputCaret } from '../../hooks/use-input-caret.js';
import { cn } from '../../lib/utils.js';
import { MessageDialog } from './alert.js';
import { CalendarMonth } from './calendar-month.js';

const DatePickerContext = createContext(null);
const DatePickerRange = ({
  className,
  startDate,
  endDate,
  onChange,
  onClick,
  disabled,
  mode = "input",
  variants = "day",
  startYear = new Date(1900, 0),
  endYear = new Date(2100, 0),
  disabledFrom,
  disabledTo,
  readonlyFrom,
  readonlyTo,
  error = false,
  setPickerOpen,
  startBtnRef,
  endBtnRef
  // disabledFromTo,
}) => {
  const { isDesktop } = useDevice();
  const rootRef = useRef(null);
  const [rangeText, setRangeText] = useState("");
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const Field = root.closest('[data-slot="field"]');
    const labelField = Field?.querySelector('[data-slot="field-label"]');
    const labelText = labelField?.querySelector(".label-text");
    if (!labelText) return;
    const clone = labelText.cloneNode(true);
    clone.querySelectorAll('[role="group"]').forEach((el) => el.remove());
    const text = clone.textContent?.trim() ?? "";
    setRangeText(text);
    console.log(root);
    console.log(startBtnRef, endBtnRef);
  }, []);
  const handleChange = (val, dir) => {
    const startFlag = dir === "start";
    console.log("?????", val);
    if (!val) {
      const newValue2 = {
        start: startFlag ? "" : startDate ?? "",
        end: !startFlag ? "" : endDate ?? ""
      };
      onChange?.(newValue2);
      return;
    }
    const valStr = variants === "day" ? common.setFormatDate(val) : common.setFormatMonth(val);
    const newValue = {
      start: startFlag ? valStr : startDate ?? "",
      end: !startFlag ? valStr : endDate ?? ""
    };
    onChange?.(newValue);
  };
  const startValue = useMemo(() => {
    console.log("#######datepicker range start value:", startDate);
    return startDate ? common.changeFormat(startDate) : void 0;
  }, [startDate]);
  const endValue = useMemo(() => {
    console.log("#######datepicker range end value:", endDate);
    return endDate ? common.changeFormat(endDate) : void 0;
  }, [endDate]);
  return /* @__PURE__ */ jsx(
    DatePickerContext.Provider,
    {
      value: {
        variants,
        mode,
        from: startValue,
        to: endValue,
        startYear,
        endYear,
        disabled,
        onClick
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          role: "group",
          ref: rootRef,
          className: cn(
            "flex items-center gap-4 rounded-[12px] border border-basic-04 w-full h-14 px-3 pl-4",
            "focus-within:border-interactive-primary-default-01 focus-within:shadow-[inset_0_0_0_1px_var(--color-border-interactive-primary-default-01)]",
            isDesktop && "h-[66px] pl-5 pr-4",
            error ? "!border-interactive-danger !shadow-[inset_0_0_0_1px_var(--color-border-interactive-danger)]" : "",
            disabled && "bg-state-disabled-02 border-state-disabled-02 pointer-events-none",
            className
          ),
          children: [
            /* @__PURE__ */ jsx(
              PickerWrapper,
              {
                range: "from",
                value: startValue,
                onChange: (val) => handleChange(val, "start"),
                disabledDate: disabledFrom,
                setPickerOpen,
                disabled: readonlyFrom,
                rangeTitle: rangeText,
                calendarBtnRef: startBtnRef
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-basic-04", children: "~" }),
            /* @__PURE__ */ jsx(
              PickerWrapper,
              {
                range: "to",
                value: endValue,
                onChange: (val) => handleChange(val, "end"),
                disabledDate: disabledTo,
                setPickerOpen,
                disabled: readonlyTo,
                rangeTitle: rangeText,
                calendarBtnRef: endBtnRef
              }
            )
          ]
        }
      )
    }
  );
};
const PickerWrapper = ({
  range,
  value,
  onChange,
  disabled,
  disabledDate,
  error = false,
  setPickerOpen,
  rangeTitle = "조회기간"
}) => {
  const contextInfo = useContext(DatePickerContext);
  const { isDesktop } = useDevice();
  const [openFlag, setOpenFlag] = useState(false);
  const [dateVal, setDateVal] = useState(void 0);
  const [dateText, setDateText] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [valueChange, setValueChange] = useState(false);
  const prevOpenFlagRef = useRef(openFlag);
  const btnRef = useRef(null);
  const { i18n } = useTranslation();
  useEffect(() => {
    setPickerOpen?.(openFlag);
    if (prevOpenFlagRef.current === openFlag) return;
    prevOpenFlagRef.current = openFlag;
    console.log("데이트피커 캘린더 openFlag:", openFlag);
    window.dispatchEvent(
      new CustomEvent("datepicker:visibility-change", {
        detail: {
          open: openFlag
        }
      })
    );
  }, [openFlag]);
  useEffect(() => {
    if (!value) return;
    setDateVal(value);
    changeDateVal(value);
  }, [value]);
  const changeDateVal = (val, confirm) => {
    const currentDate = val;
    if (!currentDate) {
      setDateText("");
      if (!valueChange) onChange?.(void 0);
      return;
    }
    const finalDate = onChange?.(currentDate);
    const next = contextInfo?.variants === "day" ? common.setFormatDate(currentDate) ?? "" : common.setFormatMonth(currentDate) ?? "";
    if (finalDate) {
      setDateText(finalDate);
      setDateVal(common.changeFormat(finalDate));
    } else {
      setDateText(next);
    }
    if (confirm === true) contextInfo?.onClick?.();
    setValueChange(false);
  };
  const checkDateInput = ({ val, type }) => {
    setPickerOpen?.(false);
    if (!val) {
      setDateVal(void 0);
      changeDateVal(void 0);
      return;
    }
    if (common.dateCheck({
      dateVal: val.replaceAll(".", ""),
      monthFlag: contextInfo?.variants === "month",
      startYear: String(contextInfo?.startYear?.getFullYear()),
      endYear: String(contextInfo?.endYear?.getFullYear())
    }) === "N") {
      if (type === "blur") {
        setAlertOpen(true);
        setDateVal(void 0);
        changeDateVal(void 0);
      } else {
        setDateText(common.formatYYYYMMDD(val));
      }
      return;
    } else {
      const newVal = common.formatYYYYMMDD(val.replaceAll(".", ""));
      const newDate = common.changeFormat(newVal);
      setDateVal(newDate);
      changeDateVal(newDate);
    }
  };
  const inputRef = useRef(null);
  const setAllValue = (value2) => {
    checkDateInput({ val: value2, type: "default" });
  };
  const { handleChange, handleKeyDown } = useInputCaret({
    inputRef,
    value: dateText,
    onValueChange: setAllValue
  });
  const openCalendar = () => {
    setOpenFlag(true);
    setPickerOpen?.(true);
  };
  const closeCalendar = () => {
    setOpenFlag(false);
    setPickerOpen?.(false);
    setTimeout(() => {
      btnRef?.current?.focus();
    }, 300);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-2 flex-1 justify-between",
          isDesktop && "gap-1"
        ),
        onClick: contextInfo?.mode === "picker" && !isDesktop ? () => setOpenFlag(true) : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              inputMode: "numeric",
              type: "text",
              ref: inputRef,
              className: cn(
                `bg-base-white border-none body-lg text-basic-02 !w-[calc(100%-34px)] ${!isDesktop && "tracking-[-.075em] outline-none"} ${error ? "border-state-danger-01" : ""}`,
                !isDesktop && "outline-none",
                disabled && "!bg-base-white",
                contextInfo?.mode === "picker" && "!bg-base-white",
                contextInfo?.mode === "picker" && isDesktop && "focus-visible:outline-none"
              ),
              value: dateText,
              disabled,
              readOnly: contextInfo?.mode === "picker",
              title: range === "from" ? `${rangeTitle} 시작일 입력` : `${rangeTitle} 종료일 입력`,
              maxLength: contextInfo?.variants === "month" ? 7 : 10,
              onChange: handleChange,
              onKeyDown: handleKeyDown,
              onBlur: (e) => {
                checkDateInput({ val: e.target.value, type: "blur" });
              },
              onFocus: () => setPickerOpen?.(true),
              placeholder: contextInfo?.variants === "day" ? "YYYY.MM.DD" : "YYYY.MM"
            }
          ),
          !disabled && /* @__PURE__ */ jsx(
            "button",
            {
              ref: btnRef,
              onClick: openCalendar,
              "aria-label": range === "from" ? `${rangeTitle} 시작일 선택` : `${rangeTitle} 종료일 선택`,
              children: /* @__PURE__ */ jsx(LineIcon, { icoColor: "bg-icon-basic-01", icoName: "ico-calendar" })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      DialogWrapper,
      {
        title: i18n.language === "ko" ? "날짜 선택" : "Date picker",
        open: openFlag,
        setOpen: setOpenFlag,
        footer: [
          {
            type: "primary",
            title: i18n.language === "ko" ? "확인" : "Confirm",
            hasClose: true,
            handler: () => {
              changeDateVal(dateVal, true);
            }
          }
        ],
        onCloseAutoFocus: closeCalendar,
        children: [
          contextInfo?.variants === "day" && /* @__PURE__ */ jsx(
            Calendar,
            {
              mode: "single",
              selected: dateVal,
              onSelect: setDateVal,
              className: "w-full",
              captionLayout: "dropdown",
              defaultMonth: dateVal,
              startMonth: contextInfo.startYear,
              endMonth: contextInfo.endYear,
              disabled: disabledDate,
              labels: {
                labelPrevious: () => "이전 달로 이동",
                labelNext: () => "다음 달로 이동"
              },
              required: true
            }
          ),
          contextInfo?.variants === "month" && /* @__PURE__ */ jsx(
            CalendarMonth,
            {
              startYear: contextInfo.startYear?.getFullYear(),
              endYear: contextInfo.endYear?.getFullYear(),
              value: dateVal,
              onChange: setDateVal,
              disabled: disabledDate
            }
          )
        ]
      }
    ),
    alertOpen && /* @__PURE__ */ jsx(
      MessageDialog,
      {
        type: "alert",
        open: alertOpen,
        openChange: setAlertOpen,
        onConfirm: () => setAlertOpen(false),
        okBtnLabel: i18n.language === "ko" ? "확인" : "Confirm",
        children: i18n.language === "ko" ? /* @__PURE__ */ jsx("div", { children: "날짜형식에 맞춰 입력해 주세요." }) : /* @__PURE__ */ jsx("div", { children: "Enter date in valid format." })
      }
    )
  ] });
};

export { DatePickerRange };
